import { PaymentMethod } from "../../../../common/enums";
import { isUSA } from "../../../../common/utils";
import { type AddressInputType } from "../../../SharedAddressInput/constants";
import { type DetailedBacsPaymentPrefObjectType } from "../../common/types";
import { type SelectedBankCountry } from "../../Context/types";
import { getIdFromCountry, getNameFromCountry, mapCountryToValueSafely } from "../../mappers";
import { BacsPaymentPrefInputFields, type BacsPaymentPrefInputType } from "./constants";

type BacsAddress = {
  __typename?: "PaymentPrefAddressObjectType";
  city: string | null;
  postalCode: string | null;
  region: string | null;
  street: string | null;
  street2: string | null;
  street3: string | null;
  country: {
    __typename?: "CountryObjectType";
    id: string;
    name: string;
    alpha2code: string | null;
    code: number | null;
  } | null;
  state: { __typename?: "StateObjectType"; code: string; id: string; name: string } | null;
};

export const stripSortCodeDashes = (value: string): string => value.replace(/-/g, "");

export const mapFormikAddressToGQLObject = (address: AddressInputType): BacsAddress => {
  const isUSACountry = isUSA(getNameFromCountry(address?.country));

  return {
    city: address?.city?.trim() || null,
    country: address?.country
      ? {
          id: getIdFromCountry(address?.country),
          name: getNameFromCountry(address?.country),
          alpha2code: null,
          code: null,
        }
      : null,
    postalCode: address?.zip?.trim() || null,
    region: !isUSACountry ? address?.province?.trim() || null : null,
    state: isUSACountry
      ? {
          __typename: "StateObjectType",
          code: address?.state?.trim() ?? "",
          id: address?.state?.trim() ?? "",
          name: "",
        }
      : null,
    street: address?.streetLine1?.trim() || null,
    street2: address?.streetLine2?.trim() || null,
    street3: null,
  };
};

export const mapFormStateToContextState = (
  values: BacsPaymentPrefInputType,
  bankCountry?: SelectedBankCountry,
): DetailedBacsPaymentPrefObjectType => {
  const accountName = values[BacsPaymentPrefInputFields.AccountName].trim();
  const sortCode = stripSortCodeDashes(values[BacsPaymentPrefInputFields.SortCode]);
  const accountNumber = values[BacsPaymentPrefInputFields.AccountNumber].trim();
  const bankName = values[BacsPaymentPrefInputFields.BankName].trim();

  const accountHolderAddress = mapFormikAddressToGQLObject(values.address);

  return {
    __typename: "BacsPaymentPrefObjectType",
    id: null,
    paymentPrefId: null,
    paymentMethod: PaymentMethod.BACS,
    accountName,
    accountNumber,
    bank: {
      __typename: "BacsBankObjectType" as const,
      name: bankName,
      sortCode,
      address: {
        __typename: "PaymentPrefAddressObjectType",
        city: null,
        postalCode: null,
        region: null,
        state: null,
        street: null,
        street2: null,
        street3: null,
        country: bankCountry
          ? {
              __typename: "CountryObjectType",
              id: bankCountry.id,
              name: bankCountry.name,
              alpha2code: bankCountry.alpha2code ?? null,
              code: null,
            }
          : null,
      },
    },
    additionalInstructions:
      values[BacsPaymentPrefInputFields.AdditionalInstructions]?.trim() || null,
    accountHolderAddress,
    bankInfoRedacted: `${bankName} account ending in ${accountNumber.slice(-4)}`,
  };
};

const mapGQLAddressToFormikState = (gqlAddress?: BacsAddress | null): AddressInputType => {
  const countryName = gqlAddress?.country?.name;

  return {
    sameAsMailingAddress: false,
    streetLine1: gqlAddress?.street ?? "",
    streetLine2: gqlAddress?.street2 ?? "",
    city: gqlAddress?.city ?? "",
    zip: gqlAddress?.postalCode ?? "",
    state: countryName && isUSA(countryName) ? gqlAddress?.state?.code ?? "" : "",
    province: countryName && !isUSA(countryName) ? gqlAddress?.region ?? "" : "",
    country: mapCountryToValueSafely(gqlAddress?.country?.id, countryName),
  };
};

export const mapContextStateToFormState = (
  input?: DetailedBacsPaymentPrefObjectType,
): BacsPaymentPrefInputType => {
  return {
    accountName: input?.accountName ?? "",
    sortCode: input?.bank?.sortCode ?? "",
    bankName: input?.bank?.name ?? "",
    accountNumber: input?.accountNumber ?? "",
    confirmAccountNumber: input?.accountNumber ?? "",
    additionalInstructions: input?.additionalInstructions ?? "",
    [BacsPaymentPrefInputFields.BankCountryValidatedCountry]: "",
    address: mapGQLAddressToFormikState(input?.accountHolderAddress),
  };
};
